/**
 * Round-trips Freemarker + HTML so a notification email body can be edited in a
 * visual (contentEditable) surface without the browser silently rewriting the
 * Freemarker.
 *
 * The problem: a WYSIWYG surface renders HTML by parsing it into a live DOM.
 * Freemarker directives (`<#if>`, `<#list>`, `<@macro>`) are not valid HTML, so
 * the parser drops or relocates them. Interpolations (`${var}`), by contrast,
 * are just text as far as the parser is concerned, so they survive the round
 * trip intact.
 *
 * So the fix only needs to protect directives and FTL comments: before handing
 * HTML to the editable surface we replace each one with an inert, non-editable
 * placeholder "chip" (`<span data-fm="…">`). The browser treats a span as an
 * atomic node and never rewrites the source we stashed in its data attribute.
 * Interpolations are deliberately left as plain, editable `${var}` text so
 * authors see and can edit them like any other text — the parser keeps them
 * verbatim whether they sit in text content or an attribute (e.g.
 * `href="${link}"`). {@link unmaskFreemarker} reverses the transform on save.
 *
 * Being a lightweight tokenizer (not a full Freemarker parser) it protects the
 * common cases — directive tags and FTL comments. Callers that need a guarantee
 * should use {@link roundTripsCleanly} to confirm a given template survives
 * mask → unmask before enabling visual editing, and fall back to the raw HTML
 * editor when it does not.
 */

/** CSS marker class shared by every Freemarker chip. */
export const FM_CHIP_CLASS = 'fm-chip';
/** Attribute that stores the exact original Freemarker source of a chip. */
export const FM_DATA_ATTR = 'data-fm';

export type FreemarkerTokenKind = 'var' | 'directive' | 'comment';

/** Matches FTL comments and directive tags — never real HTML tags, because
    those start with a letter rather than `#` or `@`. */
const FM_TAG_PATTERN = /<#--[\s\S]*?-->|<\/?[#@][a-zA-Z][^>]*>/g;

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** A short, human-friendly label rendered inside the chip. */
function chipLabel(raw: string, kind: FreemarkerTokenKind): string {
  if (kind === 'var') {
    const inner = raw.replace(/^\$\{\s*/, '').replace(/\s*\}$/, '');
    return inner.split(/[!?]/)[0].trim() || inner.trim();
  }
  if (kind === 'comment') return 'comment';
  const name = /<\/?\s*([#@][a-zA-Z0-9_.]*)/.exec(raw)?.[1] ?? 'directive';
  return /^<\s*\//.test(raw) ? `/${name.replace(/^[#@]/, '')}` : name;
}

function chip(raw: string, kind: FreemarkerTokenKind): string {
  return (
    `<span class="${FM_CHIP_CLASS} ${FM_CHIP_CLASS}--${kind}" ` +
    `contenteditable="false" ${FM_DATA_ATTR}="${escapeAttr(raw)}" ` +
    `title="${escapeAttr(raw)}">${escapeText(chipLabel(raw, kind))}</span>`
  );
}

/**
 * Replaces Freemarker directives and FTL comments with inert chips so the HTML
 * can be edited in a contentEditable surface without the browser mangling them.
 * Interpolations (`${…}`) are intentionally left untouched — the parser keeps
 * them verbatim, so they stay as plain, editable text for the author.
 */
export function maskFreemarker(html: string): string {
  const source = html || '';
  return source.replace(FM_TAG_PATTERN, match =>
    chip(match, match.startsWith('<#--') ? 'comment' : 'directive')
  );
}

/**
 * Reverses {@link maskFreemarker}: every chip becomes its exact original
 * Freemarker source again. Runs through the DOM so it works on whatever markup
 * the browser produced after editing (attribute order/quoting can differ from
 * the input), then substitutes the raw source via sentinels so directives are
 * emitted literally rather than HTML-escaped.
 */
export function unmaskFreemarker(html: string): string {
  const source = html || '';
  if (!/\bdata-fm=/.test(source)) return source;
  const doc = new DOMParser().parseFromString(source, 'text/html');
  const chips = Array.from(doc.querySelectorAll(`[${FM_DATA_ATTR}]`));
  if (!chips.length) return source;
  const raws: string[] = [];
  chips.forEach(node => {
    const sentinel = ` FM${raws.length} `;
    raws.push(node.getAttribute(FM_DATA_ATTR) ?? '');
    node.replaceWith(doc.createTextNode(sentinel));
  });
  let out = doc.body.innerHTML;
  raws.forEach((raw, i) => {
    out = out.split(` FM${i} `).join(raw);
  });
  return out;
}

/** Returns the ordered list of raw Freemarker tokens (interpolations,
    directive tags and FTL comments) in a template. Used to verify that visual
    editing neither drops nor reorders any Freemarker. */
export function extractFreemarkerTokens(html: string): string[] {
  const source = html || '';
  const tokens: string[] = [];
  source.replace(FM_TAG_PATTERN, match => {
    tokens.push(match);
    return match;
  });
  const interpolation = /\$\{[^}]*\}/g;
  let match: RegExpExecArray | null;
  while ((match = interpolation.exec(source))) tokens.push(match[0]);
  return tokens.sort();
}

/**
 * Confirms a template survives a mask → unmask cycle with every Freemarker
 * token preserved, so callers can decide whether visual editing is safe for it
 * or whether to stay in the raw HTML editor. HTML structure may be normalised
 * by the parser (e.g. a `<tbody>` inserted into a table) — that is expected and
 * harmless for email; only Freemarker fidelity is asserted here. The component
 * additionally verifies the real browser round-trip through the editable
 * iframe before enabling visual mode.
 */
export function roundTripsCleanly(html: string): boolean {
  const source = html || '';
  const before = extractFreemarkerTokens(source);
  const after = extractFreemarkerTokens(
    unmaskFreemarker(maskFreemarker(source))
  );
  return (
    before.length === after.length &&
    before.every((token, i) => token === after[i])
  );
}
